import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useDispatch, useSelector } from 'react-redux';

import { closeErrorBar, showErrorBar } from 'actions/errorBar';
import { ERROR, SUCCESS } from 'constants/error-severities';
import { selectUserIsLogged } from 'selectors/user';

const getSettingQuery = settingName => gql`
  query GetWorkspaceSettings ($names: [String!]) {
    settings: getWorkspaceSettings(names: $names) {
      ... on WorkspaceSettings {
        ${settingName}
      }
    }
  }
`;

export const useWorkspaceSetting = (settingName, options = {}) => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectUserIsLogged);

  const { loading, error, data } = useQuery(getSettingQuery(settingName), {
    ...options,
    variables: { names: [settingName] },
    onError: () => {
      const message =
        "We couldn't grab your workspace setting. Please try again.";

      dispatch(showErrorBar(ERROR, message));
      window.setTimeout(() => dispatch(closeErrorBar()), 3000);
    },
    skip: !isLoggedIn,
  });

  return {
    loading,
    error,
    value: data?.settings?.[settingName],
  };
};

const updateSettingMutation = gql`
  mutation UpdateWorkspaceSetting($name: String!, $value: BasicScalar!) {
    result: updateWorkspaceSetting(name: $name, value: $value) {
      ... on UpdatedWorkspaceSetting {
        setting {
          name
          value
        }
        created
        updated
      }
    }
  }
`;

export const useUpdateWorkspaceSetting = settingName => {
  const dispatch = useDispatch();
  const [updateSetting, { loading, error }] = useMutation(
    updateSettingMutation,
    {
      onCompleted: () => {
        const message = 'Your setting was saved successfully.';

        dispatch(showErrorBar(SUCCESS, message));
        window.setTimeout(() => dispatch(closeErrorBar()), 3000);
      },
      onError: () => {
        const message =
          'There was a problem while saving your setting. Please try again.';

        dispatch(showErrorBar(ERROR, message));
        window.setTimeout(() => dispatch(closeErrorBar()), 3000);
      },
      update: (cache, updatedData) => {
        const { settings } = cache.readQuery({
          query: getSettingQuery(settingName),
          variables: { names: [settingName] },
        });

        cache.writeQuery({
          query: getSettingQuery(settingName),
          variables: { names: [settingName] },
          data: {
            settings: {
              ...settings,
              [settingName]: updatedData.data.result.setting.value,
            },
          },
        });
      },
    }
  );

  const boundUpdate = settingValue => {
    updateSetting({
      variables: {
        name: settingName,
        value: settingValue,
      },
    });
  };

  return {
    updateSetting: boundUpdate,
    loading,
    error,
  };
};
